import React from 'react';
import { Box } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { FormSchemaV3, PLRStepV3 } from '../../../../../../../types/preloan/v3';
import PLRAlertDialog from '../../../../../PLRAlertDialog/redesign';
import type { PLRAlertState } from '../../../../../../../types/preloan';
import { partnerErrorRedirectURL } from '../../../formLogic';
import { getOrCreatePLR } from './helpers';
import analytics from '../../../../../../../utils/analytics';
import { inviteePlrCategory } from '../../../../ComplementInvite/analyticsPages';
import { useQuery } from '../../../../../../../utils/helpers';
import HTMLParser from '../../../../../../../components/HTMLParser';
import PlrShell from '../../../../landing/PlrShell';
import PlrWaiting from '../../../../landing/PlrWaiting';
import {
  PlrButton, PlrLead, PlrTitle, ProjectLabel,
} from '../../../../landing/ui';
import { plr } from '../../../../landing/tokens';
import { partnerFromSchema, useShowHero } from '../../../../landing/partner';
import { projectEntityLabel } from '../../../../heroVariant';

type GetOrCreatePLRProps = {
    formSchema: FormSchemaV3
    handleSetPLRState:(state:PLRStepV3|'error'|false)=>void,
    plrState: PLRStepV3|'error'|false,
    handleCurrentData: (values: FormSchemaV3)=> void,
    dataLoading: boolean,
    handleSetDataLoading: (state:boolean)=> void,
}

export default function GetOrCreatePLR(props:GetOrCreatePLRProps) {
  const {
    formSchema,
    handleCurrentData,
    plrState,
    handleSetPLRState,
    dataLoading,
    handleSetDataLoading,
  } = props;
  const showHero = useShowHero(formSchema);
  const [alertState, setAlertState] = React.useState<PLRAlertState|undefined>(undefined);
  const [signedLREmail, setSignedLREmail] = React.useState('');
  const handleSetAlertState = (state:PLRAlertState) => setAlertState(state);
  const handleSetSignedLREmail = (email:string) => setSignedLREmail(email);
  const query = useQuery();
  const history = useHistory();
  const searchParams = React.useMemo(() => query, [query]);

  React.useEffect(
    () => {
      setTimeout(
        () => getOrCreatePLR({
          origin: formSchema.origin,
          rut: formSchema.rut,
          captchaToken: formSchema.captchaToken,
          personalInfo: null,
          userInput: Object.values(formSchema.userInput).some((uInput) => uInput === '') ? null : formSchema.userInput,
          referralCode: formSchema.referralCode,
          complementToken: formSchema.complementToken ?? null,
          handleSetPLRState,
          handleCurrentData,
          handleSetDataLoading,
          handleSetSignedLREmail,
          handleSetAlertState,
          formSchema,
          searchParams,
          history,
          shouldCallBNPL: formSchema.shouldCallBNPL,
          invisibleCaptchaToken: formSchema.invisibleCaptchaToken,
        }),
        1000,
      );
    },
    [],
  );
  React.useEffect(() => {
    if (plrState === 'error') {
      analytics.page('PLR - ERROR INGRESO RUT', 'ERROR');
      const redirectURL = partnerErrorRedirectURL(
        formSchema.origin,
        formSchema.rut,
        formSchema.config,
      );
      if (redirectURL) {
        setTimeout(() => { window.location.href = redirectURL; }, 1000);
      }
    }
  }, [plrState]);

  const handlePostPersonalInfo = (personalInfo: FormSchemaV3['personalInfo']) => {
    setAlertState(undefined);
    handleSetDataLoading(true);
    setTimeout(
      () => getOrCreatePLR({
        origin: formSchema.origin,
        rut: formSchema.rut,
        captchaToken: personalInfo.invisibleRecaptchaToken,
        personalInfo: {
          firstName: personalInfo.firstName,
          lastName: personalInfo.lastName,
          birthDate: personalInfo.birthDate,
          sex: personalInfo.sex,
        },
        userInput: Object.values(formSchema.userInput).some((uInput) => uInput === '') ? null : formSchema.userInput,
        referralCode: formSchema.referralCode,
        complementToken: formSchema.complementToken ?? null,
        handleSetPLRState,
        handleCurrentData,
        handleSetDataLoading,
        handleSetSignedLREmail,
        handleSetAlertState,
        formSchema,
        searchParams,
        history,
        shouldCallBNPL: formSchema.shouldCallBNPL,
        invisibleCaptchaToken: formSchema.invisibleCaptchaToken,
      }),
      2000,
    );
  };

  const pageTrack = (plrDataLoading: boolean, plrAlertState: PLRAlertState|undefined) => {
    if (!plrDataLoading) {
      switch (plrAlertState) {
        case 'userHasActiveLoan':
          analytics.page('PLR - REDIRECCION RUT', 'CREDITO ACTIVO');
          break;
        case 'userHasUnsignedLoanRequest':
          analytics.page('PLR - REDIRECCION RUT', 'SOLICITUD DE CREDITO SIN FIRMAR');
          break;
        case 'userHasSignedLoanRequest':
          analytics.page('PLR - REDIRECCION RUT', 'SOLICITUD DE CREDITO FIRMADA');
          break;
        case 'userHasMissingPersonalInfo':
          analytics.page('PLR - REDIRECCION RUT', 'RUT SIN INFORMACION');
          break;
        case 'companyOwnerNotAllowed':
          analytics.page('PLR - REDIRECCION RUT', 'RUT DE PERSONA JURIDICA');
          break;
        default:
          analytics.page(
            inviteePlrCategory(formSchema.complementToken, 'PLR - ONBOARDING'),
            'PRE-CALIFICACION',
          );
          break;
      }
    }
  };

  React.useEffect(() => {
    if (alertState !== undefined) {
      pageTrack(dataLoading, alertState);
    }
  }, [dataLoading, alertState]);

  React.useEffect(() => {
    analytics.page(
      inviteePlrCategory(formSchema.complementToken, 'PLR - ONBOARDING'),
      'RESULTADO PRELIMINAR',
    );
  }, []);

  const partner = partnerFromSchema(formSchema);
  let title = 'Revisando tus datos';
  if (alertState) title = 'Por ahora no puedes continuar';
  else if (!dataLoading) title = '¡Vas por buen camino!';

  return (
    <PlrShell
      partner={partner}
      showHero={showHero}
      progress={{ step: 1, label: 'Tu RUT' }}
      testId="preloan-data-check"
    >
      {showHero && (
        <ProjectLabel hideOnPanel>
          {projectEntityLabel(formSchema.partnerEntityName, formSchema.partner)}
        </ProjectLabel>
      )}
      <PlrTitle size="step" sx={{ mt: showHero ? '-6px' : 0 }}>{title}</PlrTitle>
      {dataLoading && (
        <>
          <PlrLead>Esto toma unos segundos. No cierres esta página.</PlrLead>
          <PlrWaiting
            messages={['Revisando tu RUT…', 'Preparando tu evaluación…']}
            error={plrState === 'error'
              ? 'Ha ocurrido un error, por favor intenta en otra ocasión o contáctanos al servicio al cliente.'
              : undefined}
          />
        </>
      )}
      {!dataLoading && alertState && (
        <>
          <PlrLead>
            Según nuestros registros existe un motivo por el que no puedes continuar la
            evaluación con nosotros. Sigue las instrucciones indicadas en el detalle.
          </PlrLead>
          <PLRAlertDialog
            alertState={alertState}
            email={signedLREmail}
            formSchema={formSchema}
            handlePostPersonalInfo={handlePostPersonalInfo}
          />
        </>
      )}
      {!dataLoading && !alertState && (
        <>
          {formSchema.config.preLoanRequestConfig?.welcomeText?.secondMessage && (
            <Box
              data-pw="preloan-get-or-create-text"
              sx={{ fontSize: { xs: 15, sm: 17 }, lineHeight: 1.5, color: plr.body }}
            >
              <HTMLParser
                html={formSchema.config.preLoanRequestConfig?.welcomeText?.secondMessage}
              />
            </Box>
          )}
          <PlrButton type="submit" testId="preloan-submit-button" sx={{ mt: { xs: 'auto', sm: '8px' } }}>
            Continuar
          </PlrButton>
        </>
      )}
    </PlrShell>
  );
}
